"use client";

import { ArrowLeft, MapPin, Thermometer, Mountain, Droplet } from 'lucide-react';
import Link from 'next/link';
import { ImageWithFallback } from '@/figma/components/figma/ImageWithFallback';
import type { EducationRegionsPageContent } from '@/lib/page-content-types';

const regions = [
  {
    id: 'galilee',
    name: 'Galilee',
    subtitle: 'Upper & Lower Galilee',
    description: 'Israel\'s largest wine region, stretching from the Mediterranean to the Sea of Galilee. Diverse microclimates from sea level to 1,200m elevation.',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200',
    elevation: '200-1,200m',
    climate: 'Mediterranean to Cool Continental',
    rainfall: '500-800mm annually',
    soils: 'Terra rossa, limestone, volcanic basalt',
    keyGrapes: ['Cabernet Sauvignon', 'Syrah', 'Merlot', 'Chardonnay', 'Sauvignon Blanc'],
    characteristics: 'Cool nights preserve acidity. Elevation creates elegance. Long growing season.',
    notableWineries: ['Galil Mountain', 'Dalton', 'Recanati', 'Pelter', 'Tabor']
  },
  {
    id: 'golan-heights',
    name: 'Golan Heights',
    subtitle: 'Premium Volcanic Terroir',
    description: 'Israel\'s most prestigious region. Volcanic soils and high elevation (400-1,200m) create optimal conditions for world-class wines.',
    image: 'https://images.unsplash.com/photo-1523825391079-0092e2f2baaf?w=1200',
    elevation: '400-1,200m',
    climate: 'Cool Continental',
    rainfall: '800-1,200mm annually',
    soils: 'Volcanic basalt, well-drained',
    keyGrapes: ['Cabernet Sauvignon', 'Merlot', 'Syrah', 'Pinot Noir', 'Chardonnay'],
    characteristics: 'Volcanic minerality. Structured tannins. Excellent aging potential.',
    notableWineries: ['Golan Heights Winery', 'Bazelet HaGolan', 'Odem Mountain']
  },
  {
    id: 'judean-hills',
    name: 'Judean Hills',
    subtitle: 'Jerusalem Mountain Terroir',
    description: 'Historic region surrounding Jerusalem. High altitude vineyards (600-900m) on limestone-rich soils produce structured, age-worthy wines.',
    image: 'https://images.unsplash.com/photo-1474557157379-8aa74a6ef541?w=1200',
    elevation: '600-900m',
    climate: 'Mediterranean Mountain',
    rainfall: '500-600mm annually',
    soils: 'Limestone, terra rossa, chalk',
    keyGrapes: ['Cabernet Sauvignon', 'Merlot', 'Syrah', 'Carignan', 'Chardonnay'],
    characteristics: 'Limestone minerality. Structured wines. Old vine character.',
    notableWineries: ['Domaine du Castel', 'Tzora', 'Clos de Gat', 'Flam', 'Yatir']
  },
  {
    id: 'shomron',
    name: 'Shomron',
    subtitle: 'Samarian Hills',
    description: 'Central mountain region with diverse terroirs. Cooler microclimates and limestone soils produce elegant wines with finesse.',
    image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1200',
    elevation: '300-800m',
    climate: 'Mediterranean',
    rainfall: '400-600mm annually',
    soils: 'Limestone, chalk, terra rossa',
    keyGrapes: ['Cabernet Sauvignon', 'Merlot', 'Shiraz', 'Viognier'],
    characteristics: 'Balanced acidity. Mediterranean character. Approachable styles.',
    notableWineries: ['Tishbi', 'Segal', 'Amphora']
  },
  {
    id: 'negev',
    name: 'Negev',
    subtitle: 'Desert Edge Innovation',
    description: 'Israel\'s wine frontier. Desert-edge vineyards at 600-1,000m elevation produce intense, concentrated wines from extreme terroir.',
    image: 'https://images.unsplash.com/photo-1516594915697-87eb3b1c14ea?w=1200',
    elevation: '600-1,000m',
    climate: 'Desert Continental',
    rainfall: '200-300mm annually (irrigated)',
    soils: 'Loess, sandy loam, chalk',
    keyGrapes: ['Cabernet Sauvignon', 'Shiraz', 'Merlot', 'Grenache'],
    characteristics: 'High diurnal temperature variation. Intense fruit. Unique desert character.',
    notableWineries: ['Yatir', 'Carmey Avdat', 'Ramat Negev']
  },
  {
    id: 'sharon',
    name: 'Sharon Plain',
    subtitle: 'Coastal Mediterranean',
    description: 'Coastal plain region influenced by Mediterranean breezes. Sandy soils and moderate climate produce fresh, aromatic wines.',
    image: 'https://images.unsplash.com/photo-1543418219-44e30b057fea?w=1200',
    elevation: '0-100m',
    climate: 'Mediterranean Coastal',
    rainfall: '500-600mm annually',
    soils: 'Sandy loam, alluvial',
    keyGrapes: ['Sauvignon Blanc', 'Chardonnay', 'Cabernet Sauvignon', 'Merlot'],
    characteristics: 'Mediterranean freshness. Coastal influence. Aromatic whites.',
    notableWineries: ['Carmel', 'Tishbi', 'Alexander']
  }
];

export function RegionsPage({ content }: { content?: EducationRegionsPageContent | null }) {
  const regionsData = content?.regions?.length ? content.regions : regions;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/education"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Education
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <h1 
                className="mb-8"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-5xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Wine Regions
              </h1>
              
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-xl)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '720px'
                }}
              >
                From Mediterranean coast to desert edge, Israel&apos;s diverse topography creates six distinct wine regions, each with unique climate, soil, and character.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Regions Detail */}
      {regionsData.map((region, index) => (
        <section 
          key={region.id}
          style={{ backgroundColor: index % 2 === 0 ? 'var(--linen)' : 'var(--paper)' }}
        >
          <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
            <div className="py-24 lg:py-32">
              <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
                {/* Image */}
                <div className={index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}>
                  <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: 'var(--stone)' }}>
                    <ImageWithFallback
                      src={region.image}
                      alt={region.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}>
                  <div className="mb-4">
                    <span 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--oxblood)',
                        fontWeight: 400,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {region.subtitle}
                    </span>
                  </div>

                  <h2 
                    className="mb-6"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-4xl)',
                      fontWeight: 300,
                      lineHeight: 'var(--line-height-tight)',
                      color: 'var(--charcoal)',
                      letterSpacing: '-0.02em'
                    }}
                  >
                    {region.name}
                  </h2>

                  <div className="mb-6">
                    <Link
                      href={`/education/regions/${region.id}`}
                      className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--charcoal)',
                        fontWeight: 400
                      }}
                    >
                      View region profile
                    </Link>
                  </div>

                  <p 
                    className="mb-10"
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-base)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--slate)',
                      fontWeight: 300
                    }}
                  >
                    {region.description}
                  </p>

                  {/* Terroir Details */}
                  <div className="grid grid-cols-2 gap-6 mb-10">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Mountain size={16} style={{ color: 'var(--oxblood)' }} />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Elevation
                        </span>
                      </div>
                      <p 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {region.elevation}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Thermometer size={16} style={{ color: 'var(--oxblood)' }} />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Climate
                        </span>
                      </div>
                      <p 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {region.climate}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Droplet size={16} style={{ color: 'var(--oxblood)' }} />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Rainfall
                        </span>
                      </div>
                      <p 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {region.rainfall}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin size={16} style={{ color: 'var(--oxblood)' }} />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase'
                          }}
                        >
                          Soils
                        </span>
                      </div>
                      <p 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {region.soils}
                      </p>
                    </div>
                  </div>

                  {/* Key Grapes */}
                  <div className="mb-10">
                    <h3 
                      className="mb-4"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Key Grapes
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {region.keyGrapes.map((grape) => (
                        <span 
                          key={grape}
                          className="px-3 py-1"
                          style={{ 
                            backgroundColor: 'var(--olive-mist)',
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 400
                          }}
                        >
                          {grape}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Characteristics */}
                  <div className="mb-10 p-6" style={{ backgroundColor: index % 2 === 0 ? 'var(--paper)' : 'var(--linen)' }}>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-base)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--charcoal)',
                        fontWeight: 300,
                        fontStyle: 'italic'
                      }}
                    >
                      {region.characteristics}
                    </p>
                  </div>

                  {/* Notable Wineries */}
                  <div>
                    <h3 
                      className="mb-3"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--charcoal)',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase'
                      }}
                    >
                      Notable Wineries
                    </h3>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {region.notableWineries.join(' • ')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}
